/**
 * NAVINK PunchOut — client-side flow handler
 *
 * Flow:
 *  1. Customer clicks "Personalize via Navitor" (a.customize-button[href*="navink-punchout"])
 *  2. JS calls POST /api/navitor/punchout-setup → receives { startPageUrl, sessionId }
 *  3. Popup opens to startPageUrl; modal shows waiting state
 *  4. Customer configures product inside Navitor's configurator
 *  5. Navitor POSTs PunchOutOrderMessage to /api/navitor/punchout-return
 *  6. Server stores result keyed by sessionId
 *  7. JS polls GET /api/navitor/punchout-result/:sessionId every 2s
 *  8. On { status: 'complete', data: {...} } → add to cart → redirect to /cart
 *  9. On { status: 'cancelled' } → close modal
 *
 * Loaded only on product pages — see layout/theme.liquid.
 * Data is injected by snippets/navink-punchout-modal.liquid via window.__navinkProductData.
 */

(function () {
  'use strict';

  /** Reference to the Navitor configurator popup */
  var navitorPopup = null;

  /** setInterval handle for server-side polling */
  var pollingInterval = null;

  /* ------------------------------------------------------------------ */
  /*  Initialisation                                                      */
  /* ------------------------------------------------------------------ */

  function init() {
    if (!window.__navinkProductData) {
      console.log('[NAVINK] skipped — no __navinkProductData on this page');
      return;
    }

    console.log('[NAVINK] init OK — product:', window.__navinkProductData.productTitle);

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('popstate', onPopState);

    // Direct listener on button as belt-and-suspenders
    var btns = document.querySelectorAll('a.customize-button[href*="navink-punchout"], a[href*="navink-punchout"]');
    console.log('[NAVINK] found', btns.length, 'Personalize button(s) on page');
    btns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        console.log('[NAVINK] button clicked (direct listener)');
        openNavinkModal();
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Event handlers                                                      */
  /* ------------------------------------------------------------------ */

  function onDocumentClick(e) {
    // Close button or overlay click → close modal
    if (e.target.closest('#navink-modal-close, #navink-modal-overlay')) {
      closeNavinkModal();
      return;
    }

    // Re-open popup if customer accidentally closed it
    if (e.target.closest('#navink-waiting-reopen')) {
      if (navitorPopup && !navitorPopup.closed) {
        navitorPopup.focus();
      } else {
        openNavinkModal();
      }
      return;
    }

    // Retry button inside error panel
    if (e.target.closest('.navink-error__retry')) {
      openNavinkModal();
      return;
    }

    // "Personalize via Navitor" button — try exact class first, fall back to href match
    var btn = e.target.closest('a.customize-button') ||
              e.target.closest('a[href*="navink-punchout"]');
    if (!btn) return;
    var href = btn.getAttribute('href') || '';
    if (!href.includes('navink-punchout')) return;
    console.log('[NAVINK] button clicked (delegated listener)');
    e.preventDefault();
    e.stopPropagation();
    openNavinkModal();
  }

  function onKeyDown(e) {
    if (e.key !== 'Escape') return;
    var modal = document.getElementById('navink-modal');
    if (modal && modal.style.display === 'flex') closeNavinkModal();
  }

  function onPopState() {
    var params = new URLSearchParams(window.location.search);
    var variantId = params.get('variant');
    if (variantId && window.__navinkProductData) {
      window.__navinkProductData.variantId = parseInt(variantId, 10);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Modal lifecycle                                                     */
  /* ------------------------------------------------------------------ */

  function openNavinkModal() {
    var modal = document.getElementById('navink-modal');
    if (!modal) return;

    resetModal();
    modal.style.display = 'flex';
    document.body.classList.add('navink-modal-open');

    fetchStartPageUrl()
      .then(function (result) {
        var startPageUrl = result.startPageUrl;
        var sessionId = result.sessionId;

        // Open popup
        navitorPopup = window.open(
          startPageUrl,
          'NavinkConfigurator',
          'width=1100,height=750,scrollbars=yes,resizable=yes'
        );

        if (!navitorPopup || navitorPopup.closed) {
          showError(
            'Popup was blocked. Please allow popups for this site and try again.',
            true
          );
          return;
        }

        // Show waiting panel
        setLoading(false);
        showWaiting();

        // Watch for popup close
        if (navitorPopup) watchPopup(navitorPopup);

        // Start polling for result
        if (sessionId) startPolling(sessionId);
      })
      .catch(function (err) {
        console.error('[NAVINK] PunchOut setup error:', err);
        showError(
          'Unable to load the configurator. Please try again or call us at 1-855-536-7675.',
          true
        );
      });
  }

  function closeNavinkModal() {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }

    // Close popup if still open
    if (navitorPopup && !navitorPopup.closed) {
      navitorPopup.close();
    }
    navitorPopup = null;

    var modal = document.getElementById('navink-modal');
    if (modal) modal.style.display = 'none';
    document.body.classList.remove('navink-modal-open');
  }

  function watchPopup(popup) {
    var interval = setInterval(function () {
      if (!popup || popup.closed) {
        clearInterval(interval);
        var waiting = document.getElementById('navink-waiting');
        var loading = document.getElementById('navink-loading');
        if ((waiting && waiting.style.display !== 'none') ||
            (loading && loading.style.display !== 'none')) {
          showError('Window was closed. Please try again.', true);
        }
      }
    }, 1000);
  }

  function startPolling(sessionId) {
    var attempts = 0;
    var maxAttempts = 150; // 5 minutes at 2s intervals

    pollingInterval = setInterval(function () {
      attempts++;
      if (attempts > maxAttempts) {
        clearInterval(pollingInterval);
        pollingInterval = null;
        showError('Session expired. Please try again.', true);
        return;
      }

      fetch('https://api.5forms.shop/api/navitor/punchout-result/' + sessionId)
        .then(function (res) { return res.json(); })
        .then(function (result) {
          if (result.status === 'complete') {
            clearInterval(pollingInterval);
            pollingInterval = null;
            closeNavinkModal();
            addConfiguredItemToCart(result.data);
          } else if (result.status === 'cancelled') {
            clearInterval(pollingInterval);
            pollingInterval = null;
            closeNavinkModal();
          }
          // status === 'pending' → keep polling
        })
        .catch(function (err) {
          console.error('[NAVINK] polling error:', err);
          // Keep polling on network errors
        });
    }, 2000);
  }

  function resetModal() {
    var loadingEl = document.getElementById('navink-loading');
    var errorEl = document.getElementById('navink-error');
    var waitingEl = document.getElementById('navink-waiting');
    if (loadingEl) {
      loadingEl.style.display = 'flex';
      var p = loadingEl.querySelector('p');
      if (p) p.textContent = 'Opening configurator…';
    }
    if (errorEl) errorEl.style.display = 'none';
    if (waitingEl) waitingEl.style.display = 'none';
  }

  function showWaiting() {
    var loadingEl = document.getElementById('navink-loading');
    var waitingEl = document.getElementById('navink-waiting');
    var errorEl = document.getElementById('navink-error');
    if (loadingEl) loadingEl.style.display = 'none';
    if (errorEl) errorEl.style.display = 'none';
    if (waitingEl) waitingEl.style.display = 'flex';
  }

  function setLoading(isLoading, message) {
    var loadingEl = document.getElementById('navink-loading');
    var iframe = document.getElementById('navink-iframe');
    if (!loadingEl) return;
    loadingEl.style.display = isLoading ? 'flex' : 'none';
    if (message) {
      var p = loadingEl.querySelector('p');
      if (p) p.textContent = message;
    }
    if (iframe && !isLoading) {
      iframe.style.display = 'block';
    }
  }

  function showError(message, showRetry) {
    var errorEl = document.getElementById('navink-error');
    var loadingEl = document.getElementById('navink-loading');
    var iframe = document.getElementById('navink-iframe');
    if (loadingEl) loadingEl.style.display = 'none';
    if (iframe) iframe.style.display = 'none';
    if (!errorEl) return;
    var msgEl = errorEl.querySelector('.navink-error__message');
    var retryBtn = errorEl.querySelector('.navink-error__retry');
    if (msgEl) msgEl.textContent = message;
    if (retryBtn) retryBtn.style.display = showRetry ? 'inline-block' : 'none';
    errorEl.style.display = 'flex';
  }

  /* ------------------------------------------------------------------ */
  /*  PunchOut setup request                                              */
  /* ------------------------------------------------------------------ */

  function fetchStartPageUrl() {
    var productData = window.__navinkProductData;
    return fetch(productData.punchoutSetupUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sku: productData.sku || '',
        variantId: productData.variantId,
        returnUrl: productData.returnUrl,
      }),
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Setup request failed with HTTP ' + response.status);
        }
        return response.json();
      })
      .then(function (data) {
        if (!data.startPageUrl) {
          throw new Error('Missing startPageUrl in server response');
        }
        return { startPageUrl: data.startPageUrl, sessionId: data.sessionId };
      });
  }

  /* ------------------------------------------------------------------ */
  /*  Cart add                                                            */
  /* ------------------------------------------------------------------ */

  function addConfiguredItemToCart(navinkData) {
    var productData = window.__navinkProductData;

    setLoading(true, 'Preparing your order…');

    console.log('[NAVINK] addConfiguredItemToCart data:', {
      aspects: navinkData.aspects,
      productionTime: navinkData.productionTime,
      description: navinkData.description,
      unitPrice: navinkData.unitPrice,
    });

    // Use draft order endpoint to get correct Navitor price
    fetch('https://api.5forms.shop/api/navitor/create-draft-order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        variantId: productData.variantId,
        quantity: parseInt(navinkData.quantity || '1', 10),
        unitPrice: navinkData.unitPrice,
        lineTotal: navinkData.lineTotal,
        supplierPartId: navinkData.supplierPartId,
        supplierPartAuxiliaryId: navinkData.supplierPartAuxiliaryId,
        description: navinkData.description || navinkData.shortName || '',
        aspects: navinkData.aspects || [],
        productionTime: navinkData.productionTime || '',
        thumbnailUrl: navinkData.thumbnailUrl || '',
        unitOfMeasure: navinkData.unitOfMeasure || '',
      }),
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (errData) {
            throw new Error(errData.error || 'Failed to create order');
          });
        }
        return response.json();
      })
      .then(function (data) {
        if (!data.invoiceUrl) {
          throw new Error('No checkout URL returned');
        }
        closeNavinkModal();
        // Redirect to Shopify draft order checkout
        window.location.href = data.invoiceUrl;
      })
      .catch(function (err) {
        console.error('[NAVINK] Order error:', err);
        showError('Unable to create order: ' + err.message + '. Please try again.', true);
      });
  }

  /* ------------------------------------------------------------------ */
  /*  Boot                                                                */
  /* ------------------------------------------------------------------ */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
